import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

// Components
// Hooks
import { useFocusOutside } from '../../../common/hooks/useFocusOutside';

// Styles
import { ScrollableMixin } from '../../../common/styles/mixins';

const CustomButton = styled.button.attrs({
    type: 'button',
    className: 'btn btn-primary dropdown-toggle',
    ariaHaspopup: 'true',
    ariaExpanded: 'false'
})`
    padding-left: 0.5rem;
`;

const CustomButtonLabel = styled.span`
    margin: 0 0.6rem 0 0.5rem;
`;

const DropdownMenu = styled.div.attrs({
    className: 'dropdown-menu'
})`
    padding: 0.9rem 1rem 0;
    width: 20rem;
`;

const DropdownHeader = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 0.25rem 0;
    width: 100%;
`;

const DropdownHeaderTitle = styled.strong`
    font-size: 1.1rem;
`;

const DropdownHeaderClearAll = styled.button.attrs({
    type: 'button'
})`
    font-size: 0.9rem;
    display: inline-block;
    border: none;
    padding: 0.25rem 0rem;
    margin: 0;
    text-decoration: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    cursor: pointer;
    background: none;
    color: ${(props) => props.theme.colors.text.link};
`;

const DropdownSection = styled.div``;

const DropdownSectionTitle = styled.a`
    font-size: 0.9rem;
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    text-decoration: none !important;
    color: ${(props) => `${props.theme.colors.text.muted} !important`};
    padding: 1.2rem 0 0.8rem 0;
    position: relative;

    strong:after {
        display: block;
        position: absolute;
        right: 0;
        top: 50%;
        margin-top: -6px;
        color: ${(props) => `${props.theme.colors.icons.muted} !important`};
        font-weight: 600;
        font-size: 1.3rem;
        text-rendering: auto;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        content: '\u2039';
        transform: rotate(270deg);
        transition: all linear 0.25s;
    }
    &.collapsed strong:after {
        transform: rotate(180deg);
    }
`;

const DropdownSectionItems = styled.div`
    border-top: ${(props) => `1px solid ${props.theme.colors.monochrome.MC1}`};
    display: flex;
    flex-direction: column;
    font-size: 0.95rem;
    background: ${(props) => props.theme.colors.monochrome.MC05};
    margin: 0 -0.9rem;
    padding: 0 1.05rem;
    overflow: hidden;

    &.in,
    &.collapsing {
        border-bottom: ${(props) => `1px solid ${props.theme.colors.monochrome.MC1}`};
    }

    max-height: 15rem;
    ${ScrollableMixin}
`;

const DropdownSectionItem = styled.button.attrs({
    type: 'button'
})`
    display: flex;
    flex-direction: row;
    align-items: center;
    border: none;
    padding: 0.3rem 0rem;
    margin: 0;
    text-decoration: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    cursor: pointer;
    background: none;
    font-size: 0.85rem;
`;

const FilterIcon = styled.span.attrs({
    className: 'glyphicon glyphicon-filter',
    'aria-hidden': true
})`
    color: #fff;
    font-size: 14px;
    margin-left: 0.1rem;
`;

const DropdownSectionItemCheckbox = styled.input.attrs({
    type: 'checkbox'
})`
    scale: 1.2;
    pointer-events: none;
    margin: 2px 1rem 0 0 !important;
`;

const DropdownSectionItemLabel = styled.strong`
    text-align: left;
`;

const FlexRow = styled.div`
    display: flex;
    align-items: center;
`;

const CounterDot = styled.div`
    height: 18px;
    width: 18px;
    border-radius: 50%;
    display: flex;
    justify-content: space-around;
    align-items: center;
    background: ${(props) => (props.inverted ? props.theme.colors.brand.P5 : '#fff')};
    color: ${(props) => (props.inverted ? '#fff' : props.theme.colors.brand.P5)};
    opacity: ${({ hidden }) => (hidden ? 0 : 1)};
    font-weight: 700;
    font-size: 11px;
`;

const TableFilterMenu = ({ filterManifest, onCloseCallback, ...props }) => {
    const filterMenuRef = React.useRef(null);
    const [menuVisible, setMenuVisible] = React.useState(false);
    const [selectedFilters, setSelectedFilters] = React.useState(
        filterManifest.reduce(
            (acc, curr) => ({
                ...acc,
                ...curr.items.reduce(
                    (acc2, { value, defaultEnabled = false }) => ({
                        ...acc2,
                        [value]: {
                            enabled: defaultEnabled,
                            column: curr.column,
                            key: curr.key,
                            transformToQueryParam: curr?.transformToQueryParam
                        }
                    }),
                    {}
                )
            }),
            {}
        )
    );
    const selectedFiltersTotalCount = React.useMemo(() => {
        return Object.values(selectedFilters).filter(({ enabled }) => enabled).length;
    }, [selectedFilters]);
    const sectionsWithEnabledFilters = React.useMemo(() => {
        return filterManifest.reduce((acc, curr) => {
            return {
                ...acc,
                [curr.key]: curr.items.reduce((acc, { value }) => (selectedFilters[value].enabled ? acc + 1 : acc), 0)
            };
        }, []);
    }, [filterManifest, selectedFilters]);

    const prepareSelectedFilters = React.useCallback(() => {
        return Object.entries(selectedFilters).reduce((acc, [key, value]) => {
            if (!value.enabled) return acc;

            return {
                ...acc,
                [value.key]: {
                    key: value.key,
                    column: value.column,
                    transformToQueryParam: value?.transformToQueryParam,
                    selected: [...(acc?.[value.key]?.selected ?? []), key]
                }
            };
        }, {});
    }, [selectedFilters]);

    React.useEffect(() => {
        onCloseCallback(prepareSelectedFilters());
    }, []);

    const resetScrolls = React.useCallback(() => {
        const scrollLists = document.querySelectorAll('.table-filter-menu_scrollable-list');

        scrollLists.forEach((list) => {
            list.scrollTo(0, 0);
        });
    }, []);

    const closeMenu = React.useCallback(() => {
        if (menuVisible) {
            resetScrolls();
            setMenuVisible(false);
            onCloseCallback(prepareSelectedFilters());
        }
    }, [menuVisible, setMenuVisible, prepareSelectedFilters, onCloseCallback, resetScrolls]);

    const toggleMenu = React.useCallback(() => {
        if (!menuVisible) {
            setMenuVisible(true);
        } else {
            closeMenu();
        }
    }, [menuVisible, setMenuVisible, closeMenu]);

    const selectFilter = React.useCallback(
        (filterKey) => {
            setSelectedFilters({
                ...selectedFilters,
                [filterKey]: {
                    ...selectedFilters[filterKey],
                    enabled: !selectedFilters[filterKey].enabled
                }
            });
        },
        [setSelectedFilters, selectedFilters]
    );

    const clearAll = React.useCallback(() => {
        setSelectedFilters(
            Object.entries(selectedFilters).reduce(
                (acc, [key, value]) => ({
                    ...acc,
                    [key]: {
                        ...value,
                        enabled: false
                    }
                }),
                {}
            )
        );
    }, []);

    useFocusOutside(filterMenuRef, closeMenu);

    return (
        <div
            className={`btn-group margin-left-medium ${menuVisible ? 'open' : ''}`}
            id="tableFilterMenu"
            ref={filterMenuRef}
        >
            <CustomButton onClick={toggleMenu}>
                <FlexRow>
                    <FilterIcon />
                    <CustomButtonLabel>Filters</CustomButtonLabel>
                    {selectedFiltersTotalCount ? <CounterDot>{selectedFiltersTotalCount}</CounterDot> : null}
                </FlexRow>
            </CustomButton>
            <DropdownMenu>
                <DropdownHeader>
                    <DropdownHeaderTitle>Filters</DropdownHeaderTitle>
                    <DropdownHeaderClearAll onClick={clearAll}>Clear All</DropdownHeaderClearAll>
                </DropdownHeader>
                <DropdownSection>
                    {filterManifest.map(({ label, key, openByDefault = false, items = [] }) => (
                        <div key={key}>
                            <DropdownSectionTitle
                                data-toggle="collapse"
                                href={`#${key}`}
                                role="button"
                                aria-expanded={openByDefault}
                                aria-controls={key}
                                className={openByDefault ? '' : 'collapsed'}
                            >
                                <strong>{label}</strong>
                                {sectionsWithEnabledFilters[key] ? (
                                    <CounterDot
                                        inverted
                                        style={{
                                            marginRight: '1rem'
                                        }}
                                    >
                                        {sectionsWithEnabledFilters[key]}
                                    </CounterDot>
                                ) : null}
                            </DropdownSectionTitle>
                            <DropdownSectionItems
                                className={`table-filter-menu_scrollable-list collapse ${openByDefault ? 'in' : ''}`}
                                id={key}
                                aria-expanded={openByDefault}
                                style={openByDefault ? {} : { height: '2.22222px' }}
                            >
                                {items.map(({ label, value }, index) => (
                                    <DropdownSectionItem key={index} onClick={() => selectFilter(value)}>
                                        <DropdownSectionItemCheckbox
                                            onChange={() => {}}
                                            checked={selectedFilters[value].enabled}
                                            name={`${label}_checkbox`}
                                        />
                                        <DropdownSectionItemLabel>{label}</DropdownSectionItemLabel>
                                    </DropdownSectionItem>
                                ))}
                            </DropdownSectionItems>
                        </div>
                    ))}
                </DropdownSection>
            </DropdownMenu>
        </div>
    );
};

TableFilterMenu.propTypes = {
    filterManifest: PropTypes.array,
    onCloseCallback: PropTypes.func
};

TableFilterMenu.defaultProps = {};

export default TableFilterMenu;
